import { useEffect, useRef, useState } from 'react';

const services = [
  {
    icon: 'ri-git-branch-line',
    title: 'Workflow Automation',
    description: 'Automate repetitive internal tasks, reduce operational friction, and eliminate time-consuming manual processes that drain your team.',
    tags: ['Process Mapping', 'Trigger Logic', 'Multi-step Flows'],
    accent: '#7C3AED',
  },
  {
    icon: 'ri-robot-line',
    title: 'AI Assistants',
    description: 'Improve responsiveness and client satisfaction with intelligent AI-driven communication systems that work around the clock.',
    tags: ['Chat Automation', 'Email AI', 'Response Logic'],
    accent: '#14B8A6',
  },
  {
    icon: 'ri-database-2-line',
    title: 'CRM Optimization',
    description: 'Centralize customer data, gain full pipeline visibility, and build workflow control that keeps every deal and client on track.',
    tags: ['Pipeline Setup', 'Data Sync', 'Automations'],
    accent: '#7C3AED',
  },
  {
    icon: 'ri-filter-line',
    title: 'Lead Capture & Nurture',
    description: 'Automate intake forms, lead routing, follow-up sequences, and conversion touchpoints to turn more prospects into paying clients.',
    tags: ['Forms', 'Email Sequences', 'Routing'],
    accent: '#14B8A6',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Scheduling Systems',
    description: 'Streamline appointments, automate reminders, coordinate routing, and bring calendar chaos under control with intelligent scheduling.',
    tags: ['Booking Flows', 'Reminders', 'Calendar Sync'],
    accent: '#7C3AED',
  },
];

const ServicesSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-28 overflow-hidden" style={{ background: '#F8FAFC' }}>
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className="text-center max-w-2xl mx-auto mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="font-inter text-xs font-semibold text-brand-violet tracking-widest uppercase mb-4 block">
            Services
          </span>
          <h2
            className="font-space font-semibold text-brand-text leading-tight mb-5"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)' }}
          >
            Automation Systems Built for{' '}
            <span className="text-brand-teal">Performance</span>
          </h2>
          <p className="font-inter text-brand-steel text-base leading-relaxed">
            Every system we build is designed around how your business actually operates — not a generic template. We integrate, automate, and optimize for real outcomes.
          </p>
        </div>

        {/* Services grid — 3 top + 2 bottom centered */}
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.slice(0, 3).map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i} visible={visible} />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:max-w-2xl md:mx-auto w-full">
            {services.slice(3).map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i + 3} visible={visible} />
            ))}
          </div>
        </div>

        {/* Bottom CTA row */}
        <div
          className="mt-14 text-center"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.7s',
          }}
        >
          <p className="font-inter text-brand-steel text-sm mb-4">
            Not sure which systems you need? We&apos;ll audit your operations and recommend the right starting point.
          </p>
          <button
            onClick={() => document.querySelector('#process')?.scrollIntoView({ behavior: 'smooth' })}
            className="whitespace-nowrap font-inter font-semibold text-sm text-white px-6 py-3 rounded-lg cursor-pointer transition-all duration-200 hover:opacity-90 inline-flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}
          >
            Start With a Free Audit
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
};

interface ServiceCardProps {
  service: typeof services[0];
  index: number;
  visible: boolean;
}

const ServiceCard = ({ service, index, visible }: ServiceCardProps) => (
  <div
    className="group bg-white rounded-xl p-7 border border-brand-light hover:border-brand-violet/25 transition-all duration-300 cursor-default flex flex-col"
    style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.6s ease ${0.15 + index * 0.1}s, transform 0.6s ease ${0.15 + index * 0.1}s, border-color 0.3s`,
    }}
  >
    {/* Icon */}
    <div
      className="w-12 h-12 flex items-center justify-center rounded-xl mb-5 transition-transform duration-300 group-hover:scale-105"
      style={{ background: `${service.accent}12`, border: `1px solid ${service.accent}25` }}
    >
      <i className={`${service.icon} text-lg`} style={{ color: service.accent }} />
    </div>

    <h3 className="font-space font-semibold text-brand-text text-base mb-3">
      {service.title}
    </h3>
    <p className="font-inter text-brand-steel text-sm leading-relaxed mb-5 flex-1">
      {service.description}
    </p>

    {/* Tags */}
    <div className="flex flex-wrap gap-2">
      {service.tags.map((tag) => (
        <span
          key={tag}
          className="font-inter text-[11px] font-medium px-2.5 py-1 rounded-full"
          style={{ background: `${service.accent}10`, color: service.accent }}
        >
          {tag}
        </span>
      ))}
    </div>
  </div>
);

export default ServicesSection;
